import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { jsonLdWithMedia } from "../seo/mediaSchema";

const capabilities = [
  {
    id: "dashboard",
    title: "Live Dashboard",
    body: "Customized dashboards for post-launch operations. System status, fault counts, availability, and stoppage time, set to the view each role needs.",
  },
  {
    id: "after-action",
    title: "After Action Reports",
    body: "Automated shift-level reports with visual summaries distributed via email. No manual effort, no missed stakeholders.",
  },
  {
    id: "analytics",
    title: "Data Analytics",
    body: "Heatmaps, timelines, bar charts, and fault histograms. Self-serve tools or fully managed reporting on demand.",
  },
  {
    id: "faults",
    title: "Fault Tracking & Classification",
    body: "Full traceability from fault to root cause, resolution, and corrective action. Every event logged, owned, and closed.",
  },
  {
    id: "fleet",
    title: "Fleet Health",
    body: "Track physical condition, rental status, and history for every AMR. Includes Notes Log and Fleet Calculator for right-sizing.",
  },
  {
    id: "jobs",
    title: "Jobs Data",
    body: "Job-level performance visibility: duration, late vs. demand, and buy-off delta across all transport jobs per shift.",
  },
];

const reasons = [
  "Operational data from the fleet is streamlined after launch",
  "Customized dashboards for the manufacturer and for the team supporting the plant",
  "Post-launch management stays in one place: faults, availability, and shift results",
  "Less time spent pulling reports from separate tools",
  "The same record is used on the plant floor and by the support team",
  "Scales from one site to many",
];

const phases = [
  {
    title: "Project Kickoff",
    body: "Configure site-specific details: facility map, zones and stations, AMR types, expected job times, fault classifications, and reporting structure.",
  },
  {
    title: "Deployment",
    body: "Deploy MAG Insight inside your cloud environment. Validate access, permissions, and integration connectivity end-to-end.",
  },
  {
    title: "Training & Handoff",
    body: "Role-specific training for all user types. Stabilization support included. Your team goes live fully equipped.",
  },
];

const notes = [
  "Annual software license with M&S included",
  "Proprietary platform built in-house by MAG engineers",
  "Introductory pricing available for new engagements",
];

function CapabilitySpread() {
  const [activeId, setActiveId] = useState(capabilities[0].id);
  const active = capabilities.find((item) => item.id === activeId) ?? capabilities[0];

  return (
    <section className="grid border-t border-gray-200 lg:grid-cols-12" aria-labelledby="capabilities-heading">
      <div className="flex flex-col justify-between bg-gray-950 px-4 py-10 text-white sm:px-6 lg:col-span-5 lg:px-8 lg:py-12">
        <div>
          <p className="page-eyebrow-on-dark">Platform capabilities</p>
          <h2 id="capabilities-heading" className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Capabilities
          </h2>
          {capabilities.map((item) => {
            const selected = item.id === active.id;
            return (
              <p
                key={item.id}
                id={selected ? "maginsight-capability-panel" : undefined}
                role={selected ? "tabpanel" : undefined}
                aria-labelledby={selected ? `maginsight-tab-${item.id}` : undefined}
                className={
                  selected
                    ? "mt-5 max-h-none max-w-md text-base leading-relaxed text-white/75 opacity-100"
                    : "max-h-0 max-w-md overflow-hidden text-base leading-relaxed text-white/75 opacity-0"
                }
              >
                {item.body}
              </p>
            );
          })}
        </div>
      </div>
      <div role="tablist" aria-label="Platform capabilities" className="lg:col-span-7">
        {capabilities.map((item) => {
          const selected = item.id === active.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`maginsight-tab-${item.id}`}
              aria-selected={selected}
              aria-controls="maginsight-capability-panel"
              className={`flex w-full items-baseline gap-4 border-b border-gray-200 px-4 py-4 text-left sm:px-6 lg:px-8 ${
                selected ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50"
              }`}
              onClick={() => setActiveId(item.id)}
            >
              <span className="text-base font-semibold tracking-tight sm:text-lg">{item.title}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default function MagInsight() {
  return (
    <div className="bg-white text-gray-900">
      <Seo
        title="MAG Insight | Operational Intelligence Software | MAG"
        description="A centralized platform that consolidates AMR performance, fleet health, fault activity, and shift reporting into one system of record."
        path="/maginsight"
        jsonLd={jsonLdWithMedia(
          "/maginsight",
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "Solutions", path: "/products-services" },
            { name: "MAG Insight", path: "/maginsight" },
          ])
        )}
      />

      <section className="grid items-center lg:grid-cols-2" aria-labelledby="maginsight-heading">
        <div className="px-4 py-14 sm:px-6 lg:px-10 lg:py-16">
          <p className="page-eyebrow">Post-launch operations</p>
          <h1
            id="maginsight-heading"
            className="mt-3 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl lg:leading-[1.05]"
          >
            MAG Insight
            <sup className="ml-1 align-super text-base font-semibold tracking-normal text-gray-400 sm:text-lg">™</sup>
          </h1>
          <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
            Post-launch operational data
          </p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-gray-600">
            After launch, the fleet produces more operational data than a plant can manage by hand.
            MAG Insight streamlines that data for the manufacturer and for the team supporting the fleet.
            Customized dashboards put the right view in front of each role, so post-launch management takes less time.
          </p>
          <Link to="/contact" className="btn-primary-dark mt-8" discover="none">
            Contact Us
          </Link>
        </div>
        <figure className="bg-black">
          <video
            className="aspect-video h-auto w-full bg-black object-contain"
            controls
            playsInline
            preload="metadata"
            poster="/images/maginsight-demo.jpg"
            src="/videos/maginsight-demo.mp4"
            aria-label="MAG Insight demo"
          />
        </figure>
      </section>

      <CapabilitySpread />

      <section className="border-t border-gray-200 px-4 py-14 sm:px-6 sm:py-16 lg:px-8" aria-labelledby="why-heading">
        <h2 id="why-heading" className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Why MAG Insight
        </h2>
        <ul className="mt-8 grid gap-px bg-gray-200 sm:grid-cols-2">
          {reasons.map((reason) => (
            <li key={reason} className="bg-white px-5 py-5">
              <p className="text-base font-medium leading-relaxed text-gray-900">{reason}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-gray-200 px-4 py-14 sm:px-6 sm:py-16 lg:px-8" aria-labelledby="deployment-heading">
        <h2 id="deployment-heading" className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Deployment
        </h2>
        <ol className="mt-8 grid gap-6 lg:grid-cols-3">
          {phases.map((phase) => (
            <li key={phase.title} className="border-t-2 border-gray-900 pt-5">
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">{phase.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">{phase.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-t border-gray-900 bg-gray-950 text-white" aria-label="MAG Insight engagement">
        <ul className="grid lg:grid-cols-3">
          {notes.map((note, index) => (
            <li
              key={note}
              className={`px-4 py-10 sm:px-6 lg:px-10 ${index > 0 ? "border-t border-white/15 lg:border-l lg:border-t-0" : ""}`}
            >
              <p className="text-sm font-medium leading-relaxed text-white/80">{note}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
